<script setup>
// Combobox searchable que carrega opções via API externa configurada na
// CustomAttributeDefinition. Faz debounced fetch a cada digitação (?q=) e
// emite o valor selecionado (sempre o `value` do item, nunca o label).
import { computed, onMounted, ref, watch } from 'vue';
import { onClickOutside, useDebounceFn } from '@vueuse/core';
import attributesAPI from 'dashboard/api/attributes';

const props = defineProps({
  attributeId: { type: Number, required: true },
  isSearchable: { type: Boolean, default: false },
  modelValue: { type: [String, Number], default: '' },
  placeholder: { type: String, default: '' },
});
const emit = defineEmits(['update:modelValue']);

const open = ref(false);
const query = ref('');
const items = ref([]);
const loading = ref(false);
const rootRef = ref(null);

onClickOutside(rootRef, () => {
  open.value = false;
});

const normalizedItems = computed(() =>
  items.value.map(item =>
    typeof item === 'string' ? { label: item, value: item } : item
  )
);

const fetchItems = async () => {
  loading.value = true;
  try {
    const response = await attributesAPI.listValues(props.attributeId, {
      q: props.isSearchable && query.value ? query.value : undefined,
    });
    items.value = response?.data?.values || [];
  } finally {
    loading.value = false;
  }
};

const debouncedFetch = useDebounceFn(fetchItems, 250);

watch(query, () => {
  if (!props.isSearchable) return;
  debouncedFetch();
});

const selectItem = item => {
  emit('update:modelValue', item.value);
  query.value = item.label;
  open.value = false;
};

const onFocus = () => {
  open.value = true;
  if (items.value.length === 0) fetchItems();
};

onMounted(() => {
  if (props.modelValue) {
    query.value = String(props.modelValue);
  }
});
</script>

<template>
  <div ref="rootRef" class="relative w-full">
    <input
      v-model="query"
      type="text"
      :placeholder="placeholder"
      class="w-full px-3 py-1.5 text-sm rounded-md border border-n-weak bg-n-alpha-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
      @focus="onFocus"
    />
    <i
      v-if="loading"
      class="i-lucide-loader-2 size-3 absolute right-2 top-1/2 -translate-y-1/2 text-n-slate-10 animate-spin"
    />
    <div
      v-if="open && normalizedItems.length > 0"
      class="absolute z-50 mt-1 w-full max-h-60 overflow-y-auto rounded-lg border border-n-weak bg-n-solid-1 shadow-lg"
    >
      <button
        v-for="item in normalizedItems"
        :key="item.value"
        type="button"
        class="w-full text-left px-3 py-1.5 text-sm text-n-slate-12 hover:bg-n-alpha-1"
        @click="selectItem(item)"
      >
        <span>{{ item.label }}</span>
        <span
          v-if="item.label !== item.value"
          class="text-[10px] text-n-slate-10 ml-2"
        >
          {{ item.value }}
        </span>
      </button>
    </div>
    <div
      v-else-if="open && !loading && query"
      class="absolute z-50 mt-1 w-full rounded-lg border border-n-weak bg-n-solid-1 p-3 text-xs text-n-slate-11"
    >
      {{ $t('CUSTOM_ATTRIBUTES.FORM.ATTRIBUTE_TYPE.LIST.NO_RESULT') }}
    </div>
  </div>
</template>
